import { useState, type ReactNode } from 'react';
import {
  createFileRoute,
  Link,
  redirect,
  useCanGoBack,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Chip, Input, Panel, PanelHeader, Ref } from '@promanage/ui';
import {
  REF_PREFIX,
  formatAddress,
  formatNzDate,
  formatNzDateOr,
  formatPaymentReference,
  formatRef,
  formatShare,
  isRunning,
  parseRef,
  streetLine,
  tenancyName,
  type RefEntity,
} from '@promanage/core';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordTabs } from '../components/RecordTabs';
import { RecordCommunications } from '../features/inbox/RecordCommunications';
import { TenancyInspections } from '../features/inspections/TenancyInspections';
import { readTabSearch } from '../components/tabSearch';
import { RecordFiles } from '../features/reports/RecordFiles';
import { RecordLetters } from '../features/correspondence/RecordLetters';
import { Icon } from '../components/Icon';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { AttachRoleDialog } from '../features/people/AttachRole';
import {
  ReceiptPosted,
  ReceiptRentDialog,
  type ReceiptResult,
} from '../features/property/ReceiptRent';
import { RentPanel } from '../features/property/RentPanel';
import { BondPanel } from '../features/property/BondPanel';
import { NoticesPanel } from '../features/arrears/NoticesPanel';
import { TenancyInvoicesPanel } from '../features/tenancyInvoices/TenancyInvoicesPanel';
import { TenancyInvoicesTab } from '../features/tenancyInvoices/TenancyInvoicesTab';
import { TenancyAgreementPanel } from '../features/property/TenancyAgreement';
import { PayerSignatures } from '../features/property/PayerSignatures';
import { useOrgSettings } from '../features/settings/org';
import { TenancyPeople } from '../features/property/TenancyPeople';
import { propertyTypeLabel, spanTone, tenancyLabel } from '../features/property/shares';
import { todayIso } from '../features/property/today';
import type { TenancyDetail } from '../features/property/types';
import { trpc } from '../lib/trpc';
import { LegacyCode } from '../features/legacyCodes/LegacyCode';

/**
 * One tenancy, addressed by its display ref: /tenancies/TE000003.
 *
 * A tenancy is its own record, not a sub-heading of a building. The rent, the people and the
 * ledger account all hang off the LETTING: the same house lets for one figure this year and
 * another next, to different people, and a bond dispute is about a tenancy rather than an
 * address. This page is where all of that is read and changed.
 *
 * Same URL contract as /properties/$ref and /people/$ref — the segment is user input, so
 * `beforeLoad` normalises anything ref-shaped to the canonical form and the component answers
 * each remaining wrong shape distinctly. A ref for another entity, a typo, and a number this
 * agency has not issued are three different mistakes and only two of them are the user's to fix.
 */
export const Route = createFileRoute('/tenancies/$ref')({
  /*
   * Which section is open, in the URL. Shared with the owner, property and person pages — see
   * `readTabSearch` for why it is its own import-free module.
   */
  validateSearch: readTabSearch,

  beforeLoad: ({ params, search }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'tenancy') {
      const canonical = formatRef('tenancy', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({
          to: '/tenancies/$ref',
          params: { ref: canonical },
          // Carried over: normalising the ref must not close the tab somebody linked to.
          search,
          replace: true,
        });
      }
    }
  },
  component: TenancyDetailPage,
});

/** The entity names that take "an". The sentence below is read aloud down the phone. */
const VOWEL_ENTITIES = new Set<RefEntity>(['owner', 'invoice', 'inspection']);

function TenancyDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  if (!parsed) {
    return (
      <Unresolved title="Not a record reference">
        <code className="font-mono text-ink">{ref}</code> is not a record reference. Tenancies look
        like <code className="font-mono text-ink">TE000003</code> — two letters for the record type,
        then its number.
      </Unresolved>
    );
  }

  if (parsed.entity !== 'tenancy') {
    return (
      <Unresolved title={`${formatRef(parsed.entity, parsed.sequence)} is not a tenancy`}>
        That is {VOWEL_ENTITIES.has(parsed.entity) ? 'an' : 'a'} {parsed.entity} reference. Tenancy
        references start with <code className="font-mono text-ink">{REF_PREFIX.tenancy}</code>.
      </Unresolved>
    );
  }

  return <Detail refSeq={parsed.sequence} />;
}

function Detail({ refSeq }: { refSeq: number }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const today = todayIso();
  const [attaching, setAttaching] = useState(false);
  const [receipting, setReceipting] = useState(false);
  const [receipted, setReceipted] = useState<ReceiptResult | null>(null);

  const tenancy = useQuery(
    trpc.property.tenancy.queryOptions({ refSeq }, { staleTime: 10_000, retry: false }),
  );
  // Whether a management fee carries GST is the agency's registration, not the property's.
  const org = useOrgSettings();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.property.tenancy.queryKey({ refSeq }) });
    void queryClient.invalidateQueries({ queryKey: trpc.property.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.property.tenancies.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.rent.charges.queryKey() });
    // A tenancy's ledger account is where its rent is credited, so the ledger's own views
    // changed too — balances, journals and possibly a newly-opened period.
    invalidateAccounts(queryClient);
    void queryClient.invalidateQueries({ queryKey: trpc.ledger.journals.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.ledger.periods.queryKey() });
  };

  if (tenancy.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Tenancy" subtitle="Loading…" actions={<HeaderActions />} />
        <RecordSkeleton />
      </div>
    );
  }

  if (tenancy.error || !tenancy.data) {
    return (
      <Unresolved title={`No tenancy ${formatRef('tenancy', refSeq)}`}>
        Nothing in this agency uses that reference. It may belong to a different agency — references
        are numbered per agency, so the same code means different records elsewhere.
      </Unresolved>
    );
  }

  const record = tenancy.data;
  const subject = `${streetLine(record.property)} · ${formatRef('tenancy', record.refSeq)}`;

  return (
    <>
      <Record
        record={record}
        subject={subject}
        today={today}
        onChanged={refresh}
        onAttach={() => setAttaching(true)}
        onReceipt={() => setReceipting(true)}
        receipted={receipted}
        onDismissReceipt={() => setReceipted(null)}
      />

      {receipting ? (
        <ReceiptRentDialog
          tenancyId={record.id}
          subject={subject}
          // The rate from the property, GST from the agency's registration — composed here for
          // the preview exactly as `receiptRent` composes them for the posting.
          terms={{
            feeBp: record.property.managementFeeBp,
            gst: org.data?.gstRegistered === true,
          }}
          today={today}
          onClose={() => setReceipting(false)}
          onStepUp={() => void navigate({ to: '/mfa' })}
          onDone={(result) => {
            setReceipting(false);
            setReceipted(result);
            refresh();
          }}
        />
      ) : null}

      {attaching ? (
        <AttachRoleDialog
          target={{
            mode: 'subject',
            subject: 'tenancy',
            subjectId: record.id,
            subjectLabel: `${streetLine(record.property)} · ${formatRef('tenancy', record.refSeq)}`,
            span: { startsOn: record.startsOn, endsOn: record.endsOn },
          }}
          onClose={() => setAttaching(false)}
          onDone={() => {
            setAttaching(false);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function Record({
  record,
  subject,
  today,
  onChanged,
  onAttach,
  onReceipt,
  receipted,
  onDismissReceipt,
}: {
  record: TenancyDetail;
  subject: string;
  today: string;
  onChanged: () => void;
  onAttach: () => void;
  onReceipt: () => void;
  receipted: ReceiptResult | null;
  onDismissReceipt: () => void;
}) {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const setEnd = useMutation(
    trpc.property.setTenancyEnd.mutationOptions({
      meta: { errorTitle: 'Could not change that leaving date' },
      onSuccess: onChanged,
    }),
  );
  const propertyRef = formatRef('property', record.property.refSeq);
  const running = isRunning(record, today);
  const name = tenancyName(record.people, today);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      {/*
       * The bar says what KIND of page this is, and locates the letting: ref and address. It
       * deliberately does NOT repeat the tenancy's name from the card below — the bar is sticky,
       * so once the identity card has scrolled away this is the only thing on screen saying
       * which letting you are looking at, and an address is the identifier that still means
       * something when the tenants have changed.
       */}
      <TopBar
        title="Tenancy"
        subtitle={`${formatRef('tenancy', record.refSeq)} · ${streetLine(record.property)}`}
        actions={
          <>
            <HeaderActions />
            {/*
             * The primary action on this page, and the only one here that moves money. Offered
             * only while the tenancy runs: receipting rent against a letting that ended is
             * almost always the wrong tenancy, and the right one is a click away.
             *
             * `isRunning`, not "has no end date". A tenancy with a leaving date set is still
             * occupied and still paying rent for the weeks until it — hiding this the moment
             * notice was given would take the button away for the last month of every tenancy.
             */}
            {running ? (
              <Button size="sm" onClick={onReceipt}>
                Receipt rent
              </Button>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {receipted ? <ReceiptPosted result={receipted} onDismiss={onDismissReceipt} /> : null}
        {/* ── Identity ─────────────────────────────────────────────────────── */}
        <RecordIdentity
          title={name ?? streetLine(record.property)}
          symbol={<Icon name="tenancy" className="size-6" />}
          status={<Chip tone={spanTone(record, today)}>{tenancyLabel(record, today)}</Chip>}
          subtitle={
            <Link
              to="/properties/$ref"
              params={{ ref: propertyRef }}
              className="rounded-sm underline-offset-4 hover:text-accent hover:underline"
            >
              {formatAddress(record.property)}
            </Link>
          }
          facts={[
            { label: 'Start date', value: formatNzDate(record.startsOn) },
            { label: 'End date', value: formatNzDateOr(record.endsOn, 'Not set') },
            // The agreed term end and actual tenancy end stay distinct for early departures.
            {
              label: record.termType === 'fixed' ? 'Fixed term until' : 'Term',
              value:
                record.termType === 'fixed'
                  ? formatNzDateOr(record.fixedTermEndsOn, 'Not recorded')
                  : 'Periodic',
            },
            // Palace's tenancy (tenant group) code: it names the letting, so it is kept here.
            { label: 'Legacy code', value: <LegacyCode entity="tenancy" id={record.id} /> },
          ]}
        />

        <RecordTabs
          from="/tenancies/$ref"
          current={tab ?? 'overview'}
          label="Tenancy sections"
          invoices
          inspections
          bond
        />

        {tab === 'communications' ? (
          <RecordCommunications record={{ type: 'tenancy', id: record.id }} />
        ) : tab === 'documents' ? (
          /*
           * FULL WIDTH, not dropped into the overview’s side column. A document row carries a
           * file name, a size, a note and a withdrawal record, and a third-of-a-page column
           * truncates all four — which is the one thing an operator is reading it to check.
           */
          <div className="flex flex-col gap-4">
            {/*
             * THE AGREEMENT LEADS, and the filing cabinet follows. Somebody opening this tab is
             * usually looking for one document — the signed one — and everything below is the
             * paperwork that accumulated around it. Sorted by date it would be buried among forty
             * rows within a year of the tenancy starting.
             */}
            <TenancyAgreementPanel tenancyId={record.id} />
            <RecordFiles subject={{ type: 'tenancy', id: record.id }} />
            <RecordLetters subject={{ type: 'tenancy', id: record.id }} />
          </div>
        ) : tab === 'inspections' ? (
          /*
           * Full width, like the two beside it. A visit row carries a ref, a kind, a state, two
           * counts and a date, and a third-of-a-page column drops the chips that say whether
           * anything on it still needs somebody.
           */
          <TenancyInspections tenancyId={record.id} propertyRef={propertyRef} />
        ) : tab === 'invoices' ? (
          /*
           * Full width, for the reason Documents and Bond give above. A charge row carries a ref,
           * a description, a state, two dates, a destination and two amounts, and the description
           * — the only part that says what the tenant is being asked to pay for — is the first
           * thing a third-of-a-page column truncates.
           */
          <TenancyInvoicesTab tenancyId={record.id} today={today} />
        ) : tab === 'bond' ? (
          // Full width, same reasoning as documents above — a movement history reads left to
          // right and a third-of-a-page column would truncate the one thing this tab is for.
          <BondPanel
            tenancyId={record.id}
            subject={subject}
            today={today}
            onStepUp={() => void navigate({ to: '/mfa' })}
          />
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
                {/* Rent leads. It is what this page is opened for. */}
                <RentPanel tenancy={record} today={today} onChanged={onChanged} />

                {/* Directly under the rent, and only when there is something to say: what has been
                served about it. Silent on a tenancy that is up to date and has never been chased. */}
                <NoticesPanel tenancyId={record.id} />

                {/* And what they owe that is NOT rent. Its own panel, below the notices and
                    deliberately not folded into the rent one: a 14-day notice is about rent, and
                    an arrears figure carrying a water bill is a notice the Tribunal throws out.

                    WHAT IS STILL OWING ONLY, since the Invoices tab arrived. The whole file lives
                    there; what belongs on an overview is the part somebody is chasing, and a
                    settled charge from eighteen months ago is not it. */}
                <TenancyInvoicesPanel tenancyId={record.id} today={today} />

                {/* ── People ───────────────────────────────────────────────────── */}
                <Panel className="p-0">
                  <PanelHeader
                    title="People"
                    subtitle="Tenants, occupants and guarantors — current first, then ended roles"
                    className="px-5 pt-5 pb-3"
                    aside={
                      /*
                       * Offered on an ended tenancy too. Recording a tenant somebody missed at the
                       * time is a normal correction — and it is exactly what a disputed bond asks
                       * for, months after the keys came back.
                       */
                      <Button size="sm" variant="secondary" onClick={onAttach}>
                        Add person
                      </Button>
                    }
                  />
                  <TenancyPeople people={record.people} today={today} />
                </Panel>

                {record.notes ? (
                  <Panel className="p-0">
                    <PanelHeader title="Notes" className="px-5 pt-5 pb-3" />
                    <p className="border-t border-rule px-5 py-4 text-[0.875rem] leading-relaxed whitespace-pre-wrap text-ink-muted">
                      {record.notes}
                    </p>
                  </Panel>
                ) : null}
              </div>

              <div className="flex flex-col gap-4">
                {/* ── The letting ──────────────────────────────────────────────── */}
                <Panel className="p-0">
                  <PanelHeader title="Tenancy" className="px-5 pt-5 pb-2" />
                  <dl className="flex flex-col gap-2.5 border-t border-rule px-5 py-4 text-[0.8125rem]">
                    <Row
                      label="Reference"
                      value={<Ref entity="tenancy" sequence={record.refSeq} tone="strong" />}
                    />
                    {/*
                     * What the tenant should pay under. The ref with a check letter, so a slip
                     * typing it is caught by the matcher rather than matched to somebody else —
                     * this is the figure to read down the phone when setting up an automatic payment.
                     */}
                    <Row
                      label="Pays as"
                      value={
                        <span className="font-mono text-[0.8125rem] tabular-nums select-all">
                          {formatPaymentReference(formatRef('tenancy', record.refSeq))}
                        </span>
                      }
                    />
                    <Row
                      label="Term"
                      value={record.termType === 'fixed' ? 'Fixed term' : 'Periodic'}
                    />
                    <Row
                      label="Started"
                      value={<span className="tabular-nums">{formatNzDate(record.startsOn)}</span>}
                    />
                    <Row
                      label="Ends"
                      value={
                        <span className="tabular-nums">{formatNzDateOr(record.endsOn, '—')}</span>
                      }
                    />
                    <Row
                      label="Ledger"
                      value={
                        record.ledgerAccountId === null ? (
                          <span className="text-ink-subtle">None</span>
                        ) : (
                          <Link to="/financial" className="text-accent hover:underline">
                            Account open
                          </Link>
                        )
                      }
                    />
                  </dl>

                  {/*
                   * The leaving date lives here rather than in the top bar. Setting it closes the
                   * rent schedule, ends every role on the tenancy and takes back charges dated
                   * after the keys came back — not something to sit under the cursor beside "Back".
                   */}
                  <div className="border-t border-rule px-5 py-4">
                    <TenancyEnd
                      today={today}
                      endsOn={record.endsOn}
                      pending={setEnd.isPending}
                      onSet={(endsOn) => setEnd.mutate({ tenancyId: record.id, endsOn })}
                    />
                  </div>

                  {/*
                   * Setting a leaving date takes back the rent charged past it, INCLUDING in a
                   * month that has been signed off — the correction is dated today, so that month's
                   * reported figures do not move and its report still reconciles. What it does
                   * create is a difference between what was reported and what the ledger says now,
                   * which is a prior-period adjustment. Saying so here is what stops it being
                   * discovered at the next reconciliation.
                   */}
                  {setEnd.data && setEnd.data.rentChargesInClosedMonths > 0 ? (
                    <p role="status" className="mx-5 mb-4 text-[0.8125rem] text-warn">
                      {setEnd.data.rentChargesInClosedMonths} rent charge
                      {setEnd.data.rentChargesInClosedMonths === 1 ? '' : 's'} after that date fall
                      in a month that has been closed. They have been taken back from today, so that
                      month's reported figures stand and this shows as a prior-period adjustment on
                      the next reconciliation.
                    </p>
                  ) : null}

                  {/*
                   * Withdrawing a date puts the roles and the schedule back, but NOT the charges —
                   * the generator raises those from the schedule, and it is the one authority for
                   * what has been charged. Saying so is the difference between "the rent is missing"
                   * and "the rent is about to be raised again".
                   */}
                  {setEnd.data && setEnd.data.endsOn === null ? (
                    <p role="status" className="mx-5 mb-4 text-[0.8125rem] text-ink-muted">
                      Back on an open term. {setEnd.data.rolesReopened} role
                      {setEnd.data.rolesReopened === 1 ? '' : 's'} and{' '}
                      {setEnd.data.rentSchedulesReopened} rent schedule
                      {setEnd.data.rentSchedulesReopened === 1 ? '' : 's'} reopened. Rent charges
                      withdrawn earlier are raised again on the next generation.
                    </p>
                  ) : null}
                </Panel>

                {/*
                 * ── Known payers ──────────────────────────────────────────────
                 *
                 * Under the letting, above the property: it is a fact about how THIS tenancy's rent
                 * arrives, learnt from its own matched payments. Kept off the rent panel deliberately
                 * — the rent is what is owed, and this is what the bank happens to call the money
                 * that pays it.
                 */}
                <PayerSignatures tenancyId={record.id} />

                {/* ── The property ─────────────────────────────────────────────── */}
                <Panel className="p-0">
                  <PanelHeader
                    title="Property"
                    className="px-5 pt-5 pb-2"
                    aside={
                      <Link to="/properties/$ref" params={{ ref: propertyRef }}>
                        <Button size="sm" variant="ghost">
                          Open
                        </Button>
                      </Link>
                    }
                  />
                  <dl className="flex flex-col gap-2.5 border-t border-rule px-5 py-4 text-[0.8125rem]">
                    <Row
                      label="Reference"
                      value={
                        <Ref entity="property" sequence={record.property.refSeq} tone="strong" />
                      }
                    />
                    <Row label="Type" value={propertyTypeLabel(record.property.type)} />
                    <Row
                      label="Managed"
                      value={
                        <span className="tabular-nums">
                          {formatNzDate(record.property.managementStartsOn)} –{' '}
                          {formatNzDateOr(record.property.managementEndsOn, 'present')}
                        </span>
                      }
                    />
                  </dl>

                  {/* Whose money this tenancy's rent becomes. */}
                  <div className="border-t border-rule px-5 py-4">
                    <h3 className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                      Owners today
                    </h3>
                    <ul className="mt-1.5 flex flex-col gap-1.5">
                      {record.property.owners.map((interest) => (
                        <li
                          key={interest.ownerId}
                          className="flex items-baseline justify-between gap-3"
                        >
                          <span className="min-w-0 truncate text-[0.8125rem]">{interest.name}</span>
                          <span className="shrink-0 text-[0.8125rem] font-semibold tabular-nums">
                            {formatShare(interest.shareBp)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Panel>
              </div>
            </div>

            <p className="px-1 text-[0.75rem] text-ink-subtle">
              Rent charges are an accrual, not a journal — they are what arrears is measured against
              and nothing here has been posted to the trust ledger. Receipting a payment against a
              tenancy is the next slice of M1; until then rent received is entered by hand at{' '}
              <Link to="/financial/new" className="text-accent hover:underline">
                /financial/new
              </Link>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** A label/value pair in a details list. */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
        {label}
      </dt>
      <dd className="min-w-0 text-right">{value}</dd>
    </div>
  );
}

/**
 * Back, then the list.
 *
 * Back is hidden rather than disabled when there is no in-app history: the ref is addressable,
 * so arriving by pasted URL in a fresh tab is a real entry path, and there a browser back would
 * throw the user out of the app entirely.
 */
function HeaderActions() {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <>
      {canGoBack ? (
        <Button variant="secondary" size="sm" onClick={() => router.history.back()}>
          <Icon name="arrow-right" size={14} className="rotate-180" />
          Back
        </Button>
      ) : null}
      <Link to="/tenancies">
        <Button variant="secondary" size="sm">
          All tenancies
        </Button>
      </Link>
    </>
  );
}

/**
 * Ending a tenancy asks for the date rather than assuming today.
 *
 * The last day of a tenancy is almost never the day somebody gets round to recording it, and an
 * off-by-a-week end date is an off-by-a-week rent liability. The default is today because that
 * is the common case; it is a field because the common case is not the only one.
 */
/**
 * The tenancy's leaving date — set it, move it, or withdraw it.
 *
 * ONE CONTROL FOR ALL THREE, because it is one fact. The old version could only ever end a
 * tenancy, which made the two ordinary corrections impossible: notice retracted, and a date
 * typed wrong. Both were previously fixed by starting a SECOND tenancy for the same people in
 * the same house, which splits their rent history across two refs and turns a bond claim into
 * two conversations.
 *
 * Withdrawing is deliberately a separate, plainer button rather than "clear the field and save":
 * it puts roles and a rent schedule back, and that should read as a decision.
 */
function TenancyEnd({
  today,
  endsOn,
  pending,
  onSet,
}: {
  today: string;
  /** The date currently recorded, or null while the tenancy runs on an open term. */
  endsOn: string | null;
  pending: boolean;
  onSet: (endsOn: string | null) => void;
}) {
  const [draft, setDraft] = useState(endsOn ?? today);
  const [open, setOpen] = useState(false);

  const close = () => {
    setOpen(false);
    // Back to what the record says, so re-opening the editor never shows a half-typed date
    // from a change somebody abandoned.
    setDraft(endsOn ?? today);
  };

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          {endsOn === null ? 'End tenancy' : 'Change leaving date'}
        </Button>
        {endsOn !== null ? (
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onSet(null)}>
            {pending ? 'Saving' : 'Withdraw'}
          </Button>
        ) : null}
        <p className="mt-1 w-full text-[0.75rem] text-ink-subtle">
          {endsOn === null
            ? 'Ending closes the rent schedule and every role on this tenancy, and withdraws rent charged past that day. The record is kept — a TE ref has been quoted on a bond form.'
            : 'Moving the date carries the roles and the rent schedule with it. Withdrawing it puts the tenancy back on an open term, for notice that was retracted or a date typed wrong.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        type="date"
        aria-label="Last day of the tenancy"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-[9.5rem]"
      />
      <Button size="sm" disabled={pending || draft === ''} onClick={() => onSet(draft)}>
        {pending ? 'Saving' : 'Confirm'}
      </Button>
      <Button size="sm" variant="ghost" onClick={close}>
        Cancel
      </Button>
      <p className="mt-1 w-full text-[0.75rem] text-ink-subtle">
        The last day of the tenancy, inclusive — the day the keys come back.
      </p>
    </div>
  );
}

/**
 * The URL did not resolve to a tenancy.
 *
 * Three messages rather than one 404, for the same reason /people/$ref has three: a ref for
 * another entity, a typo, and a number this agency has not issued are different mistakes.
 */
function Unresolved({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Tenancy"
      title={title}
      back={{ label: 'Back to tenancies', link: { to: '/tenancies' } }}
    >
      {children}
    </RecordNotFound>
  );
}
